import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ArrowUp, ArrowDown, Mail, Clock, Wrench, Upload, Play, X, CheckCircle, List, Network, Save, Loader2, GripVertical } from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import apiClient from '../utils/apiClient';
import workflowsService from '../services/workflowsService';
import WorkflowCanvas from './WorkflowCanvas';

const STEP_TYPES = [
  { value: 'email', label: 'Email', icon: Mail, hint: 'Generate + send a filled form, alert managers' },
  { value: 'timer', label: 'Timer', icon: Clock, hint: 'Wait N days' },
  { value: 'external', label: 'External', icon: Wrench, hint: 'Operator does something off-system (+ optional wait)' },
];
const typeMeta = (t) => STEP_TYPES.find((s) => s.value === t) || STEP_TYPES[0];

// Optional mapping of a step to a legal lien stage. When the step runs, the
// engine stamps rental.lien_stage so the Auctions table reflects progress.
// Leave blank for steps that don't change the stage.
const LIEN_STAGE_OPTIONS = [
  { value: '', label: '— no lien stage —' },
  { value: 'pre_lien', label: 'Pre-lien Notice' },
  { value: 'lien_notice', label: 'Lien Notice' },
  { value: 'auction_scheduled', label: 'Auction Scheduled' },
  { value: 'auction_held', label: 'Auction Held' },
  { value: 'unit_cleared', label: 'Unit Cleared' },
];

const blankStep = (order) => ({
  key: `s${order}_${Math.random().toString(36).slice(2, 7)}`,
  order, type: 'email', label: '', wait_days: 0,
  subject: '', body: '', send_to_tenant: true, alert_managers: true,
  instructions: '', lien_stage: '', form_file_id: null, recipient_roles: [], next_keys: [],
});

const input = 'w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500';

const WorkflowBuilder = () => {
  const { currentFacility } = useFacility();
  const facilityId = currentFacility?._id;
  const [tab, setTab] = useState('templates'); // templates | runs
  const [templates, setTemplates] = useState([]);
  const [runs, setRuns] = useState([]);
  const [pills, setPills] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited
  const [editorView, setEditorView] = useState('canvas'); // canvas (default) | list
  const [selectedKey, setSelectedKey] = useState(null);
  const [error, setError] = useState(null);
  const [savingKey, setSavingKey] = useState(null); // step key currently saving
  const [savedKey, setSavedKey] = useState(null);   // step key recently saved (checkmark)

  const load = useCallback(async () => {
    if (!facilityId) return;
    try {
      const [t, r, p] = await Promise.all([
        workflowsService.listTemplates(facilityId),
        workflowsService.listRuns(facilityId),
        workflowsService.pills(facilityId),
      ]);
      setTemplates(t.data); setRuns(r.data); setPills(p.data);
    } catch (e) { console.error(e); }
  }, [facilityId]);
  useEffect(() => { load(); }, [load]);

  const newTemplate = () => setEditing({ name: '', kind: 'generic', description: '', require_signed_lease: false, steps: [blankStep(1)] });
  const editTemplate = async (id) => {
    const { data } = await workflowsService.getTemplate(facilityId, id);
    setEditing(data);
  };

  const saveTemplate = async () => {
    setError(null);
    try {
      const payload = { name: editing.name, kind: editing.kind || 'generic',
        description: editing.description || '',
        require_signed_lease: Boolean(editing.require_signed_lease),
        steps: editing.steps };
      if (editing._id) await workflowsService.updateTemplate(facilityId, editing._id, payload);
      else await workflowsService.createTemplate(facilityId, payload);
      setEditing(null); load();
    } catch (e) { setError(e.response?.data?.detail || 'Could not save workflow.'); }
  };

  // step editing helpers
  const setStep = (i, patch) => setEditing((e) => {
    const steps = [...e.steps]; steps[i] = { ...steps[i], ...patch }; return { ...e, steps };
  });
  const addStep = () => setEditing((e) => ({ ...e, steps: [...e.steps, blankStep(e.steps.length + 1)] }));
  const removeStep = (i) => setEditing((e) => {
    const steps = e.steps.filter((_, j) => j !== i).map((s, j) => ({ ...s, order: j + 1 }));
    return { ...e, steps };
  });
  const moveStep = (i, dir) => setEditing((e) => {
    const j = i + dir; if (j < 0 || j >= e.steps.length) return e;
    const steps = [...e.steps]; [steps[i], steps[j]] = [steps[j], steps[i]];
    return { ...e, steps: steps.map((s, k) => ({ ...s, order: k + 1 })) };
  });
  const uploadForm = async (i, file) => {
    try {
      const { data } = await workflowsService.uploadForm(facilityId, file);
      setStep(i, { form_file_id: data.file_id, form_filename: data.filename });
    } catch (e) { setError('Upload failed.'); }
  };

  // Explicit per-step Save: persists THIS step immediately by writing the whole
  // template's steps array (the existing save path). Autosave on "Save workflow"
  // still works; this just lets the operator lock in one step without leaving.
  const buildPayload = (e) => ({
    name: e.name, kind: e.kind || 'generic', description: e.description || '',
    require_signed_lease: Boolean(e.require_signed_lease), steps: e.steps,
  });
  const saveStep = async (i) => {
    setError(null);
    const e = editing;
    if (!e.name?.trim()) { setError('Name the workflow before saving a step.'); return; }
    const key = e.steps[i]?.key;
    setSavingKey(key); setSavedKey(null);
    try {
      if (e._id) {
        await workflowsService.updateTemplate(facilityId, e._id, buildPayload(e));
      } else {
        // First save also creates the template so the step is durable; keep
        // editing the now-persisted template (capture its _id).
        const { data } = await workflowsService.createTemplate(facilityId, buildPayload(e));
        setEditing((cur) => ({ ...cur, _id: data._id }));
      }
      setSavedKey(key);
      setTimeout(() => setSavedKey((k) => (k === key ? null : k)), 2000);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not save this step.');
    } finally {
      setSavingKey((k) => (k === key ? null : k));
    }
  };

  // Insert a pill token (e.g. "{{total_due}}") into a step text field at the
  // caret/drop position. `field` is 'subject' | 'body'. `el` is the target
  // input/textarea so we can read its selection range; falls back to append.
  const insertPill = (i, field, chip, el) => {
    setEditing((e) => {
      const steps = [...e.steps];
      const cur = steps[i][field] || '';
      let start = cur.length, end = cur.length;
      if (el && typeof el.selectionStart === 'number') {
        start = el.selectionStart; end = el.selectionEnd;
      }
      const next = cur.slice(0, start) + chip + cur.slice(end);
      steps[i] = { ...steps[i], [field]: next };
      // Restore caret just after the inserted chip on the next tick.
      if (el) {
        const pos = start + chip.length;
        setTimeout(() => { try { el.focus(); el.setSelectionRange(pos, pos); } catch (_) {} }, 0);
      }
      return { ...e, steps };
    });
  };
  // HTML5 drop handler: read the dragged chip, insert at the drop caret.
  const onPillDrop = (i, field) => (ev) => {
    ev.preventDefault();
    const chip = ev.dataTransfer.getData('text/plain');
    if (!chip) return;
    const el = ev.target;
    // Place caret where the drop landed (supported in most browsers).
    if (document.caretRangeFromPoint && el && typeof el.setSelectionRange === 'function') {
      const r = document.caretRangeFromPoint(ev.clientX, ev.clientY);
      if (r && typeof r.startOffset === 'number') {
        try { el.setSelectionRange(r.startOffset, r.startOffset); } catch (_) {}
      }
    }
    insertPill(i, field, chip, el);
  };
  const allowDrop = (ev) => { ev.preventDefault(); ev.dataTransfer.dropEffect = 'copy'; };

  if (!facilityId) return <div className="py-10 text-center text-[13px] text-slate-500">Select a facility.</div>;

  // ---------- TEMPLATE EDITOR ----------
  if (editing) {
    return (
      <div className="max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-slate-900">{editing._id ? 'Edit' : 'New'} Workflow</h2>
          <button onClick={() => setEditing(null)} className="text-[13px] text-slate-500">Cancel</button>
        </div>
        <div className="space-y-3 mb-5">
          <input className={input} placeholder="Workflow name (e.g. Lien — Online Auction)"
            value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} />
          <input className={input} placeholder="Description (optional)"
            value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
          <label className="flex items-center gap-2 text-[13px] text-slate-600">
            <input type="checkbox" checked={Boolean(editing.require_signed_lease)}
              onChange={(e) => setEditing({ ...editing, require_signed_lease: e.target.checked })} />
            Require a signed lease before this workflow can start (legal gate for lien-type processes)
          </label>
        </div>

        {/* pill reference — draggable chips */}
        <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 p-3">
          <p className="text-[11px] font-medium text-slate-500 mb-1.5">Merge fields — <strong>drag a chip</strong> onto a step's subject or body to insert it, or click to copy. Name your fillable-PDF fields to match these tokens and they fill per tenant at send time.</p>
          <div className="flex flex-wrap gap-1.5">
            {pills.map((p) => (
              <button key={p.token} title={`${p.label} — drag onto subject/body, or click to copy`}
                draggable
                onDragStart={(ev) => { ev.dataTransfer.setData('text/plain', p.chip); ev.dataTransfer.effectAllowed = 'copy'; }}
                onClick={() => navigator.clipboard?.writeText(p.chip)}
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-slate-200 text-[11px] text-slate-600 hover:border-blue-400 cursor-grab active:cursor-grabbing">
                <GripVertical className="h-2.5 w-2.5 text-slate-300" />
                {p.chip}
              </button>
            ))}
          </div>
        </div>

        {/* view toggle: list builder vs visual canvas */}
        <div className="flex items-center gap-1.5 mb-3">
          <button onClick={() => setEditorView('list')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium ${editorView === 'list' ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
            <List className="h-3.5 w-3.5" /> List
          </button>
          <button onClick={() => setEditorView('canvas')}
            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[12px] font-medium ${editorView === 'canvas' ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
            <Network className="h-3.5 w-3.5" /> Canvas
          </button>
        </div>

        {editorView === 'canvas' && (
          <div className="mb-4">
            <WorkflowCanvas
              steps={editing.steps}
              onChange={(steps) => setEditing((e) => ({ ...e, steps }))}
              selectedKey={selectedKey}
              onSelect={setSelectedKey}
              onAddStep={addStep}
            />
            {selectedKey && (() => {
              const i = editing.steps.findIndex((s) => s.key === selectedKey);
              if (i < 0) return null;
              const s = editing.steps[i];
              return (
                <div className="mt-3 rounded-xl border border-blue-200 bg-blue-50/40 p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-[11px] font-semibold text-slate-400">Step #{i + 1}</span>
                    <select className="rounded-lg border border-slate-200 px-2 py-1 text-[12px]"
                      value={s.type} onChange={(e) => setStep(i, { type: e.target.value })}>
                      {STEP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <input className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-[13px]"
                      placeholder="Step label" value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} />
                    <button onClick={() => { removeStep(i); setSelectedKey(null); }} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                  <label className="block text-[12px] text-slate-500 mb-2">Wait days before this step
                    <input type="number" min="0" className={input} value={s.wait_days}
                      onChange={(e) => setStep(i, { wait_days: Number(e.target.value) })} /></label>
                  <label className="block text-[12px] text-slate-500 mb-2">Lien stage (optional) — advances the tenant's stage when this step runs
                    <select className={input} value={s.lien_stage || ''}
                      onChange={(e) => setStep(i, { lien_stage: e.target.value || null })}>
                      {LIEN_STAGE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select></label>
                  {s.type === 'email' && (
                    <div className="space-y-2">
                      <input className={input} placeholder="Email subject (drag pills here)" value={s.subject || ''}
                        onChange={(e) => setStep(i, { subject: e.target.value })}
                        onDrop={onPillDrop(i, 'subject')} onDragOver={allowDrop} />
                      <textarea className={input} rows="2" placeholder="Email body (drag pills here)" value={s.body || ''}
                        onChange={(e) => setStep(i, { body: e.target.value })}
                        onDrop={onPillDrop(i, 'body')} onDragOver={allowDrop} />
                      <label className="flex items-center gap-2 text-[12px] text-slate-500 cursor-pointer">
                        <Upload className="h-3.5 w-3.5" />
                        <span>{s.form_filename || (s.form_file_id ? 'Form uploaded' : 'Upload fillable PDF (optional)')}</span>
                        <input type="file" accept="application/pdf,.pdf,.md" className="hidden"
                          onChange={(e) => e.target.files[0] && uploadForm(i, e.target.files[0])} />
                      </label>
                    </div>
                  )}
                  {s.type === 'external' && (
                    <textarea className={input} rows="2" placeholder="Instructions for the operator" value={s.instructions || ''} onChange={(e) => setStep(i, { instructions: e.target.value })} />
                  )}
                  <div className="mt-3"><StepSaveButton stepKey={s.key} savingKey={savingKey} savedKey={savedKey} onSave={() => saveStep(i)} /></div>
                </div>
              );
            })()}
          </div>
        )}

        <div className={`space-y-3 ${editorView === 'canvas' ? 'hidden' : ''}`}>
          {editing.steps.map((s, i) => {
            const M = typeMeta(s.type); const Icon = M.icon;
            return (
              <div key={s.key} className="rounded-xl border border-slate-200 bg-white p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[11px] font-semibold text-slate-400">#{i + 1}</span>
                  <Icon className="h-4 w-4 text-slate-400" />
                  <select className="rounded-lg border border-slate-200 px-2 py-1 text-[12px]"
                    value={s.type} onChange={(e) => setStep(i, { type: e.target.value })}>
                    {STEP_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                  <input className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-[13px]"
                    placeholder="Step label" value={s.label} onChange={(e) => setStep(i, { label: e.target.value })} />
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveStep(i, -1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => moveStep(i, 1)} className="p-1 text-slate-400 hover:text-slate-700"><ArrowDown className="h-3.5 w-3.5" /></button>
                    <button onClick={() => removeStep(i)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
                <p className="text-[11px] text-slate-400 mb-2">{M.hint}</p>

                <div className="grid grid-cols-2 gap-2 mb-2">
                  <label className="text-[12px] text-slate-500">Wait days before this step
                    <input type="number" min="0" className={input} value={s.wait_days}
                      onChange={(e) => setStep(i, { wait_days: Number(e.target.value) })} />
                  </label>
                </div>

                {s.type === 'email' && (
                  <div className="space-y-2">
                    <input className={input} placeholder="Email subject (drag pills here)"
                      value={s.subject || ''} onChange={(e) => setStep(i, { subject: e.target.value })}
                      onDrop={onPillDrop(i, 'subject')} onDragOver={allowDrop} />
                    <textarea className={input} rows="2" placeholder="Email body (drag pills here) — keep it short and point to the attached PDF"
                      value={s.body || ''} onChange={(e) => setStep(i, { body: e.target.value })}
                      onDrop={onPillDrop(i, 'body')} onDragOver={allowDrop} />
                    <div className="flex items-center gap-3 text-[12px]">
                      <label className="flex items-center gap-1"><input type="checkbox" checked={s.send_to_tenant}
                        onChange={(e) => setStep(i, { send_to_tenant: e.target.checked })} /> Send to tenant</label>
                      <label className="flex items-center gap-1"><input type="checkbox" checked={s.alert_managers}
                        onChange={(e) => setStep(i, { alert_managers: e.target.checked })} /> Alert managers</label>
                    </div>
                    <label className="block text-[12px] text-slate-500">Reminder days after send (comma-separated, e.g. 4,8,12,16,20 — re-sends until the next step runs)
                      <input className={input}
                        value={(s.reminder_days || []).join(',')}
                        onChange={(e) => setStep(i, {
                          reminder_days: e.target.value.split(',')
                            .map((x) => parseInt(x.trim(), 10))
                            .filter((n) => Number.isFinite(n) && n > 0),
                        })} />
                    </label>
                    <label className="flex items-center gap-2 text-[12px] text-slate-500 cursor-pointer">
                      <Upload className="h-3.5 w-3.5" />
                      <span>{s.form_filename || (s.form_file_id ? 'Form uploaded' : 'Upload fillable PDF (optional)')}</span>
                      <input type="file" accept="application/pdf,.pdf,.md" className="hidden"
                        onChange={(e) => e.target.files[0] && uploadForm(i, e.target.files[0])} />
                    </label>
                  </div>
                )}
                {s.type === 'external' && (
                  <textarea className={input} rows="2" placeholder="Instructions for the operator (e.g. place newspaper ad)"
                    value={s.instructions || ''} onChange={(e) => setStep(i, { instructions: e.target.value })} />
                )}
                <div className="mt-3 flex justify-end">
                  <StepSaveButton stepKey={s.key} savingKey={savingKey} savedKey={savedKey} onSave={() => saveStep(i)} />
                </div>
              </div>
            );
          })}
        </div>

        {editorView === 'list' && (
          <button onClick={addStep} className="mt-3 flex items-center gap-1.5 text-[13px] text-blue-600 hover:underline">
            <Plus className="h-3.5 w-3.5" /> Add step
          </button>
        )}

        {error && <p className="text-[13px] text-red-500 mt-3">{error}</p>}
        <div className="mt-5 flex gap-2">
          <button onClick={saveTemplate} className="rounded-lg bg-blue-600 text-white text-[13px] font-medium px-5 py-2 hover:bg-blue-700">Save workflow</button>
          <button onClick={() => setEditing(null)} className="text-[13px] text-slate-500 px-3">Cancel</button>
        </div>
      </div>
    );
  }

  // ---------- LIST VIEW (templates / runs tabs) ----------
  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        {['templates', 'runs'].map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 rounded-lg text-[13px] font-medium ${tab === t ? 'bg-blue-600 text-white' : 'bg-slate-50 text-slate-500 hover:bg-slate-100'}`}>
            {t === 'templates' ? 'Templates' : 'Active Runs'}
          </button>
        ))}
        {tab === 'templates' && (
          <button onClick={newTemplate} className="ml-auto flex items-center gap-1.5 rounded-lg bg-blue-600 text-white text-[13px] font-medium px-4 py-2 hover:bg-blue-700">
            <Plus className="h-3.5 w-3.5" /> New Workflow
          </button>
        )}
      </div>

      {tab === 'templates' ? (
        <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead><tr className="bg-slate-50/50 border-b border-slate-100">
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase">Workflow</th>
                <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase">Steps</th>
                <th></th></tr></thead>
              <tbody className="divide-y divide-slate-50">
                {templates.length === 0 ? (
                  <tr><td colSpan="3" className="px-4 py-8 text-center text-[13px] text-slate-400">No workflows yet — build one to automate a multi-step process.</td></tr>
                ) : templates.map((t) => (
                  <tr key={t._id} className="hover:bg-slate-50/50">
                    <td className="px-4 py-3 text-[13px] font-medium text-slate-700">{t.name}</td>
                    <td className="px-4 py-3 text-[13px] text-slate-500">{(t.steps || []).length}</td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => editTemplate(t._id)} className="text-[12px] text-blue-600 hover:underline">Edit</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <WorkflowRuns facilityId={facilityId} runs={runs} reload={load} />
      )}
    </div>
  );
};

// Inline form shown when completing a step that defines completion_fields.
// Renders each field (text/date/datetime/select/checkbox), honoring show_if so
// only the relevant inputs appear (e.g. online-sale dates vs onsite date-time).
const StepCompletionForm = ({ fields, onSubmit, onCancel }) => {
  const [values, setValues] = useState({});
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));
  const active = (f) => !f.show_if ||
    String(values[f.show_if.field] ?? '') === String(f.show_if.equals);
  const submit = () => {
    const missing = fields.filter((f) => active(f) && f.required &&
      (values[f.key] === undefined || values[f.key] === '' || values[f.key] === false))
      .map((f) => f.label || f.key);
    if (missing.length) { alert('Required: ' + missing.join(', ')); return; }
    const out = {};
    fields.forEach((f) => { if (active(f) && values[f.key] !== undefined) out[f.key] = values[f.key]; });
    onSubmit(out);
  };
  return (
    <div className="mt-2 ml-24 p-3 rounded-lg border border-slate-200 bg-white space-y-2 max-w-md">
      {fields.filter(active).map((f) => (
        <div key={f.key} className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-slate-500">
            {f.label}{f.required && <span className="text-red-400"> *</span>}
          </label>
          {f.type === 'select' ? (
            <select value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
              className="text-[13px] border border-slate-200 rounded px-2 py-1">
              <option value="">Select…</option>
              {(f.options || []).map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : f.type === 'checkbox' ? (
            <input type="checkbox" checked={!!values[f.key]}
              onChange={(e) => set(f.key, e.target.checked)} className="h-4 w-4" />
          ) : (
            <input
              type={f.type === 'date' ? 'date' : f.type === 'datetime' ? 'datetime-local' : 'text'}
              value={values[f.key] || ''} onChange={(e) => set(f.key, e.target.value)}
              className="text-[13px] border border-slate-200 rounded px-2 py-1" />
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <button onClick={submit} className="text-[12px] px-3 py-1 rounded bg-blue-600 text-white hover:bg-blue-700">Complete step</button>
        <button onClick={onCancel} className="text-[12px] px-3 py-1 rounded text-slate-500 hover:bg-slate-50">Cancel</button>
      </div>
    </div>
  );
};

const WorkflowRuns = ({ facilityId, runs, reload }) => {
  const [expanded, setExpanded] = useState(null);
  const [completing, setCompleting] = useState(null); // `${runId}:${key}` when a form is open
  const markDone = async (runId, key, values) => {
    try { await workflowsService.markStepDone(facilityId, runId, key, values); setCompleting(null); reload(); }
    catch (e) { alert(e.response?.data?.detail || 'Could not complete the step'); }
  };
  const cancel = async (runId) => {
    if (!window.confirm('Cancel this workflow run?')) return;
    try { await workflowsService.cancelRun(facilityId, runId); reload(); } catch (e) { /* */ }
  };
  return (
    <div className="rounded-xl border border-slate-200/80 bg-white overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead><tr className="bg-slate-50/50 border-b border-slate-100">
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase">Tenant / Unit</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase">Status</th>
            <th className="text-left px-4 py-2.5 text-[11px] font-semibold text-slate-400 uppercase">Started</th>
            <th></th></tr></thead>
          <tbody className="divide-y divide-slate-50">
            {runs.length === 0 ? (
              <tr><td colSpan="4" className="px-4 py-8 text-center text-[13px] text-slate-400">No active runs.</td></tr>
            ) : runs.map((r) => (
              <React.Fragment key={r._id}>
                <tr className="hover:bg-slate-50/50 cursor-pointer" onClick={() => setExpanded(expanded === r._id ? null : r._id)}>
                  <td className="px-4 py-3">
                    <div className="text-[13px] font-medium text-slate-700">
                      {r.customer_name || 'Unknown tenant'}
                      {r.unit_number && <span className="text-slate-400 font-normal"> · Unit {r.unit_number}</span>}
                    </div>
                    <div className="text-[11px] text-slate-400">{r.template_name}</div>
                  </td>
                  <td className="px-4 py-3"><span className="px-2 py-0.5 text-[10px] font-medium rounded-full bg-blue-50 text-blue-600">{r.status}</span></td>
                  <td className="px-4 py-3 text-[13px] text-slate-400">{(r.anchor_date || '').split('T')[0]}</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <span className="text-[12px] text-blue-600">{expanded === r._id ? 'Hide' : 'Steps'}</span>
                    {r.status === 'active' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); cancel(r._id); }}
                        className="ml-4 text-[12px] text-red-500 hover:underline"
                      >Cancel</button>
                    )}
                  </td>
                </tr>
                {expanded === r._id && (
                  <tr><td colSpan="4" className="px-4 py-3 bg-slate-50/40">
                    {(() => {
                      const ordered = [...(r.steps || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
                      return ordered.map((s) => {
                      const hasFields = (s.completion_fields || []).length > 0;
                      const formOpen = completing === `${r._id}:${s.key}`;
                      // The active step is the one the run is executing (status
                      // 'due') — show it as "executing". Any executing step (a
                      // waiting timer or a manual gate) gets a Mark-done action.
                      const isExecuting = s.status === 'due';
                      const statusLabel = isExecuting ? 'executing' : s.status;
                      const statusCls = s.status === 'done'
                        ? 'bg-emerald-50 text-emerald-600'
                        : isExecuting ? 'bg-amber-50 text-amber-600'
                        : 'bg-slate-50 text-slate-400';
                      return (
                      <div key={s.key} className="py-0.5">
                        <div className="flex items-center gap-3 text-[13px]">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${statusCls}`}>{statusLabel}</span>
                          <span className="text-slate-400 w-20">{s.due_date}</span>
                          <span className="text-slate-700 flex-1">
                            {s.label}
                            {(s.reminders_sent || []).length > 0 && (
                              <span className="text-[10px] text-slate-400 ml-2">reminders: day {(s.reminders_sent || []).join(', ')}</span>
                            )}
                          </span>
                          {/* Whatever step is executing gets a Mark-done action
                              (a timer ends its wait; a gate completes). Steps with
                              required inputs open a Complete… form instead. */}
                          {isExecuting && r.status === 'active' && (
                            <button
                              onClick={() => hasFields ? setCompleting(formOpen ? null : `${r._id}:${s.key}`) : markDone(r._id, s.key)}
                              className="text-[12px] text-blue-600 hover:underline">
                              {hasFields ? (formOpen ? 'Close' : 'Complete…') : 'Mark as done'}
                            </button>
                          )}
                        </div>
                        {formOpen && (
                          <StepCompletionForm
                            fields={s.completion_fields}
                            onSubmit={(vals) => markDone(r._id, s.key, vals)}
                            onCancel={() => setCompleting(null)} />
                        )}
                      </div>
                      );
                    });
                    })()}
                    {r.status === 'active' && (
                      <div className="pt-2">
                        <button onClick={() => cancel(r._id)} className="text-[12px] text-red-500 hover:underline">Cancel run</button>
                      </div>
                    )}
                  </td></tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Per-step Save button with saving / saved feedback. Persists the step
// immediately (whole-template save under the hood) without leaving the editor.
const StepSaveButton = ({ stepKey, savingKey, savedKey, onSave }) => {
  const saving = savingKey === stepKey;
  const saved = savedKey === stepKey;
  return (
    <button type="button" onClick={onSave} disabled={saving}
      className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-[12px] font-medium transition-colors ${
        saved ? 'bg-emerald-50 text-emerald-600 border border-emerald-200'
              : 'bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100'} ${saving ? 'opacity-70' : ''}`}>
      {saving ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</>
        : saved ? <><CheckCircle className="h-3.5 w-3.5" /> Saved</>
        : <><Save className="h-3.5 w-3.5" /> Save step</>}
    </button>
  );
};

export default WorkflowBuilder;
